import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import BotonAcceso from "../elementos/BotonAcceso.jsx";
import { useTheme } from "../guardarToken/themeContext";
import { MapPinned, RefreshCw, ArrowLeft } from 'lucide-react';
import { CircularProgress } from "@mui/material";

const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES || '/api/direcciones/addres/';
const apiUrlSedes = process.env.REACT_APP_API_SEDES || '/api/sedes/';

const EditAddres = () => {
    const { id_direccion } = useParams();
    const [direccion, setNewDireccion] = useState("");
    const [localidad, setNewLocalidad] = useState('');
    const [sede_id, setSedeId] = useState('');
    const [sedes, setSedes] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [addrRes, sedesRes] = await Promise.all([
                    axios.get(`${apiUrlDirecciones}${id_direccion}`),
                    axios.get(apiUrlSedes)
                ]);

                const direccionData = addrRes.data;
                setNewDireccion(direccionData.direccion);
                setNewLocalidad(direccionData.localidad);
                setSedeId(direccionData.sede_id || '');
                setSedes(sedesRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id_direccion]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await axios.put(`${apiUrlDirecciones}${id_direccion}`, {
                direccion,
                localidad,
                sede_id: sede_id || null
            });
            navigate('/admin/AbmDirecciones');
        } catch (error) {
            console.error('Error al actualizar direccion:', error);
        }
    };

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                <CircularProgress color="primary" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
            <div className={`w-full max-w-2xl ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-100"} rounded-3xl shadow-2xl border p-8 animate-in zoom-in duration-300`}>

                <div className="flex flex-col items-center mb-8">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 p-4 rounded-2xl shadow-lg shadow-emerald-900/30 mb-4">
                        <MapPinned className="text-white w-8 h-8" />
                    </div>
                    <h2 className="text-2xl font-black tracking-tight mb-1">Editar Dirección</h2>
                    <p className={`text-xs font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Actualizar vinculación de ubicación</p>
                </div>

                <form className="space-y-6" onSubmit={handleUpdate}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Sede Perteneciente</label>
                            <select
                                value={sede_id}
                                onChange={(e) => setSedeId(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"
                                    } border`}
                                required
                            >
                                <option value="">Seleccione una sede</option>
                                {sedes.map(s => (
                                    <option key={s.id} value={s.id}>{s.nombre}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Dirección</label>
                            <input
                                type="text"
                                placeholder="Dirección"
                                value={direccion}
                                onChange={(e) => setNewDireccion(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"
                                    } border`}
                                required
                            />
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Localidad</label>
                            <input
                                type="text"
                                placeholder="Localidad"
                                value={localidad}
                                onChange={(e) => setNewLocalidad(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"
                                    } border`}
                                required
                            />
                        </div>
                    </div>

                    <div className="flex gap-4 pt-6">
                        <BotonAcceso
                            variant="accent"
                            type='submit'
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-widest h-14 rounded-2xl shadow-xl shadow-emerald-900/20 flex items-center justify-center gap-2 transform active:scale-95 transition-all"
                        >
                            <RefreshCw className="w-4 h-4" /> Actualizar Datos
                        </BotonAcceso>
                        <BotonAcceso
                            variant="dark"
                            type='button'
                            onClick={() => navigate('/admin/AbmDirecciones')}
                            className={`px-8 ${isDarkMode ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600"} font-black text-xs uppercase tracking-widest h-14 rounded-2xl flex items-center justify-center gap-2 transition-all`}
                        >
                            <ArrowLeft className="w-4 h-4" /> Volver
                        </BotonAcceso>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditAddres;
